import React, { useContext } from 'react';
import { GraduationCap, Phone, Bot, Brain, Cog, Gift, ShieldCheck, Check, Lock, Star, PartyPopper, Minus, X as XIcon } from 'lucide-react';
import { EditCtx, PageCtx, Sec, Tx, Cta, Editable, VideoFrame, RichSpan, Ext } from './EditableBits';
import { googleCalUrl, downloadIcs } from '../ThreeJobs';

const KIT_ICONS = [GraduationCap, Phone, Bot, Brain, Cog, Gift];

/* ── shared page header (logo + progress dots + step label) ── */
export const StepHeaderBlock = ({ id, p }) => (
  <Sec id={id} as="header" className="flex flex-col items-center px-6 pt-8 pb-2" bg={p.bg}>
    <img src="/voxinity-logo-light.png?v=8" alt="Voxinity AI" className="h-7 w-auto mb-6" />
    <div className="flex items-center gap-2" data-testid="funnel-progress-dots">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`w-8 h-1.5 rounded-full ${i < (p.dots ?? 3) ? 'bg-[#141414]' : 'bg-black/[0.1]'}`} />
      ))}
    </div>
    <Tx v={p.step_label} sec={id} k="step_label" base="arc-body text-xs font-bold uppercase tracking-[0.25em] text-[#141414]/40 mt-3" />
    <Ext sec={id} p={p} at="end" />
  </Sec>
);

/* ═══ KIT ($27) page blocks ═══ */
export const KitIntroBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pt-10" bg={p.bg}>
    <div className="max-w-2xl mx-auto">
      <Tx v={p.headline} sec={id} k="headline" as="h1" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight text-center mb-4" strongClass="font-black" testid="kit-headline" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.subhead} sec={id} k="subhead" base="arc-body text-base md:text-lg text-[#141414]/60 text-center mb-10" strongClass="font-bold text-[#141414]" />
      <Ext sec={id} p={p} at="subhead" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const KitOfferBlock = ({ id, p }) => {
  const { handlers } = useContext(PageCtx);
  const bump = !!handlers.bump;
  return (
    <Sec id={id} className="px-6" bg={p.bg}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-start">
        {/* LEFT: the offer */}
        <div>
          <div className="rounded-3xl border border-black/[0.06] bg-white overflow-hidden mb-6 shadow-[0_12px_40px_rgba(20,20,20,0.08)]" data-testid="kit-stack-card">
            <div className="bg-[#141414] px-6 py-5">
              <Tx v={p.stack_title} sec={id} k="stack_title" as="h2" base="arc-head font-black text-lg text-[#F0EFEC] text-center tracking-wide" />
            </div>
            <div className="divide-y divide-black/[0.05]">
              {(p.items || []).map((row, i) => {
                const Icon = KIT_ICONS[i % KIT_ICONS.length];
                return (
                  <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                    <span className="w-9 h-9 rounded-2xl bg-[#141414]/[0.05] flex items-center justify-center shrink-0">
                      <Icon size={15} className="text-[#141414]" />
                    </span>
                    <p className="arc-body text-sm font-medium text-[#141414]/75 flex-1">
                      <Tx v={row.item} sec={id} k={`items.${i}.item`} as="span" base="" />
                    </p>
                    <span className="arc-body text-sm font-bold text-[#141414]/35 line-through whitespace-nowrap">
                      <Tx v={row.value} sec={id} k={`items.${i}.value`} as="span" base="" />
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-3.5 border-t border-black/[0.05] flex items-center justify-between">
              <Tx v={p.total_label} sec={id} k="total_label" as="span" base="arc-body text-sm font-bold text-[#141414]/50" />
              <Tx v={p.total_value} sec={id} k="total_value" as="span" base="arc-body text-base font-bold text-[#141414]/35 line-through" />
            </div>
            <div className="px-6 py-4 bg-[#141414] flex items-center justify-between">
              <Tx v={p.price_label} sec={id} k="price_label" as="span" base="arc-head font-black text-base text-[#F0EFEC]" />
              <Tx v={p.price_display} sec={id} k="price_display" as="span" base="arc-head font-black text-3xl text-[#F0EFEC]" testid="kit-price" />
            </div>
          </div>

          {/* ORDER BUMP */}
          <button
            onClick={() => handlers.setBump && handlers.setBump(!bump)}
            className={`w-full text-left rounded-3xl border-2 bg-white p-5 transition-all ${bump ? 'border-[#141414] shadow-[0_8px_30px_rgba(20,20,20,0.1)]' : 'border-black/[0.1] hover:border-[#141414]/40'}`}
            data-testid="kit-bump-checkbox"
          >
            <div className="flex items-start gap-3.5">
              <span className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${bump ? 'bg-[#141414] border-[#141414]' : 'border-black/20 bg-white'}`}>
                {bump && <Check size={15} className="text-[#F0EFEC]" />}
              </span>
              <div>
                <p className="arc-body text-base font-bold text-[#141414]">
                  <Tx v={p.bump_title} sec={id} k="bump_title" as="span" base="" />
                </p>
                <p className="arc-body text-sm text-[#141414]/55 mt-1">
                  <Tx v={p.bump_desc} sec={id} k="bump_desc" as="span" base="" strongClass="font-bold text-[#141414]/70" />
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* RIGHT: checkout */}
        <div className="lg:sticky lg:top-6" data-testid="kit-checkout-panel">
          <div className="rounded-3xl border border-black/[0.06] bg-white overflow-hidden shadow-[0_12px_40px_rgba(20,20,20,0.08)]">
            <div className="px-6 py-4 border-b border-black/[0.05] flex items-center justify-between">
              <span className="arc-body inline-flex items-center gap-2 text-sm font-bold text-[#141414]">
                <Lock size={13} /> <Tx v={p.checkout_label} sec={id} k="checkout_label" as="span" base="" />
              </span>
              <span className="arc-body text-sm font-black text-[#141414]" data-testid="kit-checkout-total">Total: ${bump ? 74 : 27}</span>
            </div>
            <div className="relative min-h-[420px]">{handlers.checkoutSlot || null}</div>
          </div>

          <div className="flex items-center justify-center gap-2 flex-wrap mt-4" data-testid="kit-checkout-trust">
            <span className="arc-body inline-flex items-center gap-1.5 text-xs font-semibold text-[#141414]/55">
              <Lock size={12} /> Guaranteed safe &amp; secure checkout
            </span>
            <span className="text-[#141414]/20">·</span>
            <span className="arc-body text-xs font-semibold text-[#141414]/55">Powered by <span className="font-black text-[#141414]/75">Stripe</span></span>
            <span className="text-[#141414]/20">·</span>
            <span className="arc-body inline-flex items-center gap-1.5 text-xs font-semibold text-[#141414]/55">
              <ShieldCheck size={12} /> <Tx v={p.guarantee_text} sec={id} k="guarantee_text" as="span" base="" />
            </span>
          </div>
          <Tx v={p.encrypt_note} sec={id} k="encrypt_note" base="arc-body text-xs text-[#141414]/40 text-center mt-2" />

          <div className="rounded-3xl border border-black/[0.06] bg-white p-5 shadow-sm mt-4" data-testid="kit-checkout-review">
            <div className="flex items-center gap-1 mb-1.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={13} className="text-amber-400" fill="currentColor" />)}
            </div>
            <Tx v={p.review_text} sec={id} k="review_text" base="arc-body text-sm text-[#141414]/70 leading-relaxed italic" />
            <Tx v={p.review_author} sec={id} k="review_author" base="arc-body text-xs font-bold text-[#141414]/45 mt-1.5" />
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto"><Ext sec={id} p={p} at="end" /></div>
    </Sec>
  );
};

export const KitDeclineBlock = ({ id, p }) => {
  const { handlers } = useContext(PageCtx);
  return (
    <Sec id={id} className="px-6 pb-10" bg={p.bg}>
      <div className="max-w-6xl mx-auto text-center mt-12">
        <button onClick={handlers.decline} disabled={handlers.declining} className="arc-body text-sm text-[#141414]/40 hover:text-[#141414]/60 underline underline-offset-2 disabled:opacity-50" data-testid="kit-decline-link">
          {handlers.declining ? 'One moment…' : <Tx v={p.decline_text} sec={id} k="decline_text" as="span" base="" />}
        </button>
        <Tx v={p.urgency_text} sec={id} k="urgency_text" base="arc-body text-xs text-[#141414]/35 mt-2" testid="kit-urgency" />
        <Ext sec={id} p={p} at="end" />
      </div>
    </Sec>
  );
};

/* Static stand-in for the Stripe payment form inside the editor preview */
export const KitMockPay = () => (
  <div className="p-6 min-h-[420px] flex flex-col items-center justify-center text-center" data-testid="kit-mock-pay">
    <span className="w-14 h-14 rounded-2xl bg-[#141414]/[0.05] flex items-center justify-center mb-4">
      <Lock size={20} className="text-[#141414]/60" />
    </span>
    <p className="arc-body text-sm font-bold text-[#141414]/70">Stripe secure checkout renders here</p>
    <p className="arc-body text-xs text-[#141414]/40 mt-1 mb-6 max-w-[260px]">Card fields load on the live page. The amount charged is set by the payment system.</p>
    <span className="arc-body w-full rounded-full bg-[#141414] text-[#F0EFEC] text-lg font-bold px-8 py-4 opacity-60 flex items-center justify-center gap-2">
      <Lock size={16} /> Pay $27 — Get Instant Access
    </span>
  </div>
);

/* ═══ THANK YOU page blocks ═══ */
export const ThanksHeroBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pt-10" bg={p.bg}>
    <div className="max-w-2xl mx-auto text-center mb-10">
      <span className="inline-flex w-16 h-16 rounded-full bg-[#141414] items-center justify-center mb-5">
        <PartyPopper size={24} className="text-[#F0EFEC]" />
      </span>
      <Tx v={p.headline} sec={id} k="headline" as="h1" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight mb-3" strongClass="font-black" testid="thanks-headline" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.subhead} sec={id} k="subhead" base="arc-body text-base text-[#141414]/60" strongClass="font-bold text-[#141414]" />
      <Ext sec={id} p={p} at="subhead" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

/* The three thank-you jobs — also rendered (compact) inside the VIP page */
export const ThreeJobsInner = ({ sec, p, compact }) => {
  const { config } = useContext(PageCtx);
  const gcal = googleCalUrl(config);
  return (
    <div className={`space-y-5 ${compact ? '' : 'max-w-xl mx-auto'}`} data-testid="thank-you-three-jobs">
      <div className="rounded-2xl border border-black/[0.08] bg-white p-6" data-testid="ty-calendar-card">
        <Tx v={p.s1_label} sec={sec} k="s1_label" base="arc-body text-xs font-bold uppercase tracking-[0.2em] text-[#141414]/45 mb-2" />
        <Tx v={p.s1_title} sec={sec} k="s1_title" as="h3" base="arc-head font-black text-lg text-[#141414] mb-4" />
        <div className="flex flex-wrap gap-3">
          {gcal ? (
            <>
              <a href={gcal} target="_blank" rel="noreferrer" className="arc-body rounded-full bg-[#141414] text-[#F0EFEC] text-sm font-bold px-5 py-2.5 hover:bg-black transition-colors" data-testid="ty-google-cal-btn">
                Google Calendar
              </a>
              <button onClick={() => downloadIcs(config)} className="arc-body rounded-full bg-white border border-black/[0.12] text-[#141414] text-sm font-bold px-5 py-2.5 hover:bg-black/[0.03] transition-colors" data-testid="ty-apple-cal-btn">
                Apple / Outlook (.ics)
              </button>
            </>
          ) : (
            <span className="arc-body rounded-full bg-black/[0.05] text-[#141414]/50 text-sm font-bold px-5 py-2.5" data-testid="ty-cal-tba">
              Date announced soon — watch your email
            </span>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-black/[0.08] bg-white p-6" data-testid="ty-community-card">
        <Tx v={p.s2_label} sec={sec} k="s2_label" base="arc-body text-xs font-bold uppercase tracking-[0.2em] text-[#141414]/45 mb-2" />
        <Tx v={p.s2_title} sec={sec} k="s2_title" as="h3" base="arc-head font-black text-lg text-[#141414] mb-2" />
        <Tx v={p.s2_body} sec={sec} k="s2_body" base="arc-body text-sm text-[#141414]/60 mb-4" />
        <a href={config.community_url} target="_blank" rel="noreferrer" className="arc-body inline-block rounded-full bg-[#141414] text-[#F0EFEC] text-sm font-bold px-5 py-2.5 hover:bg-black transition-colors" data-testid="ty-community-btn">
          <Tx v={p.s2_cta} sec={sec} k="s2_cta" as="span" base="" />
        </a>
      </div>

      <div className="rounded-2xl border border-black/[0.08] bg-white p-6" data-testid="ty-homework-card">
        <Tx v={p.s3_label} sec={sec} k="s3_label" base="arc-body text-xs font-bold uppercase tracking-[0.2em] text-[#141414]/45 mb-2" />
        <VideoFrame
          url={p.video_url}
          sec={sec} k="video_url"
          className="rounded-xl overflow-hidden bg-[#141414] mb-4"
          testid="ty-homework-video"
          placeholder={(
            <div className="aspect-video flex flex-col items-center justify-center">
              <span className="w-14 h-14 rounded-full bg-[#F0EFEC] flex items-center justify-center mb-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#141414"><path d="M8 5v14l11-7z" /></svg>
              </span>
              <span className="arc-body text-xs font-bold uppercase tracking-[0.2em] text-[#F0EFEC]/50">Video coming soon</span>
            </div>
          )}
        />
        <Tx v={p.s3_body} sec={sec} k="s3_body" base="arc-body text-sm text-[#141414]/70 leading-relaxed" strongClass="font-bold text-[#141414]" />
      </div>
    </div>
  );
};

export const ThreeJobsBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pb-10" bg={p.bg}>
    <div className="max-w-2xl mx-auto">
      <ThreeJobsInner sec={id} p={p} />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

/* ═══ VIP page blocks ═══ */
export const VipHeroBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pt-10" bg={p.bg}>
    <div className="max-w-6xl mx-auto text-center mb-10">
      <Tx v={p.headline} sec={id} k="headline" as="h1" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight mb-3" strongClass="font-black" testid="vip-headline" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.subhead} sec={id} k="subhead" base="arc-body text-base text-[#141414]/60 max-w-xl mx-auto" strongClass="font-bold text-[#141414]" />
      <Ext sec={id} p={p} at="subhead" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const VipMainBlock = ({ id, p }) => {
  const { handlers, jobs } = useContext(PageCtx);
  const edit = useContext(EditCtx);
  return (
    <Sec id={id} className="px-6 pb-10" bg={p.bg}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-start">
        <div className="relative">
          <EditCtx.Provider value={null}>
            <ThreeJobsInner sec="__jobs" p={jobs || {}} compact />
          </EditCtx.Provider>
          {edit && (
            <div className="absolute inset-0 z-10 flex items-start justify-center pt-4 bg-white/[0.02]" onClick={(e) => { e.stopPropagation(); edit.onSelect(id, null); }}>
              <span className="rounded-full bg-[#141414]/85 text-white text-xs font-bold px-4 py-2 shadow-lg">These 3 steps are edited on the Thank You page</span>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-black/[0.08] bg-white p-7 shadow-[0_16px_50px_rgba(20,20,20,0.1)] lg:sticky lg:top-6" data-testid="vip-onboarding-card">
          <Editable sec={id} k="banner_text">
            <div className="rounded-xl bg-[#141414] px-4 py-3.5 mb-5 flex items-center gap-3" data-testid="vip-included-banner">
              <Gift size={17} className="text-[#F0EFEC] shrink-0" />
              <p className="arc-body text-sm font-bold text-[#F0EFEC]"><RichSpan v={p.banner_text} strongClass="font-black" /></p>
            </div>
          </Editable>
          <Tx v={p.card_title} sec={id} k="card_title" as="h2" base="arc-head font-black text-2xl text-[#141414] mb-2" />
          <Tx v={p.card_body} sec={id} k="card_body" base="arc-body text-sm text-[#141414]/60 mb-6" strongClass="font-bold text-[#141414]" />
          {handlers.vipSlot || null}
        </div>
      </div>
      <div className="max-w-6xl mx-auto"><Ext sec={id} p={p} at="end" /></div>
    </Sec>
  );
};

const MOCK_PLANS = [
  { id: 'launchpad', name: 'Free', price: '$0/mo', who: 'Your first client', note: '1 client sub-account' },
  { id: 'starter', name: 'Starter', price: '$197/mo', who: 'Your first few clients', note: '3 client sub-accounts · BYOK' },
  { id: 'agency', name: 'Agency', price: '$397/mo', who: 'Build a real agency — under YOUR brand', note: '10 client sub-accounts · white-label', popular: true },
  { id: 'pro', name: 'Pro', price: '$897/mo', who: 'Scaling mode', note: 'Unlimited sub-accounts · white-label' },
];

/* Static stand-in for the activation form inside the editor preview */
export const VipMockForm = ({ sec, p }) => (
  <div className="space-y-5" data-testid="vip-mock-form">
    <div>
      <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Your login email</label>
      <div className="arc-body w-full px-4 py-3 rounded-xl bg-black/[0.04] border border-black/[0.08] text-[#141414]/50 font-medium">buyer@example.com</div>
    </div>
    <div>
      <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Create your password</label>
      <div className="arc-body w-full px-4 py-3 rounded-xl bg-white border border-black/[0.12] text-[#141414]/35 font-medium">••••••••</div>
    </div>
    <div>
      <Tx v={p.plan_label} sec={sec} k="plan_label" as="label" base="arc-body block text-sm font-bold text-[#141414]/70 mb-2" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {MOCK_PLANS.map((pl) => (
          <div key={pl.id} className={`text-left rounded-xl border p-3.5 ${pl.popular ? 'border-[#141414] bg-[#141414]/[0.04]' : 'border-black/[0.08]'}`}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="arc-head font-black text-sm text-[#141414]">{pl.name}</span>
              {pl.popular && <span className="arc-body text-[9px] font-bold tracking-wide bg-[#141414] text-[#F0EFEC] rounded-full px-2 py-0.5">RECOMMENDED</span>}
            </div>
            <div className="arc-body text-sm font-bold text-[#141414]/70">{pl.price}</div>
            <div className="arc-body text-[11px] font-semibold text-[#141414]/70 mt-1">{pl.who}</div>
            <div className="arc-body text-[11px] text-[#141414]/45">{pl.note}</div>
          </div>
        ))}
      </div>
      <Tx v={p.includes_text} sec={sec} k="includes_text" base="arc-body text-xs text-[#141414]/55 leading-relaxed mt-3" strongClass="font-bold text-[#141414]" testid="vip-plans-includes" />
    </div>
    <Editable sec={sec} k="why_card">
      <div className="rounded-xl bg-black/[0.04] border border-black/[0.06] px-4 py-3" data-testid="vip-why-card">
        <p className="arc-body text-xs text-[#141414]/65 leading-relaxed"><RichSpan v={p.why_card} strongClass="font-bold text-[#141414]" /></p>
      </div>
    </Editable>
    <Editable sec={sec} k="cta_label">
      <span className="arc-body w-full rounded-full bg-[#141414] text-[#F0EFEC] text-base font-bold px-8 py-4 flex items-center justify-center shadow-[0_12px_40px_rgba(20,20,20,0.25)]" data-testid="vip-activate-btn">
        <RichSpan v={p.cta_label} />
      </span>
    </Editable>
    <div className="text-center">
      <span className="arc-body text-xs text-[#141414]/50 inline-flex items-center gap-1.5 justify-center">
        <ShieldCheck size={12} /> <Tx v={p.trust_line} sec={sec} k="trust_line" as="span" base="" />
      </span>
      <Tx v={p.subnote} sec={sec} k="subnote" base="arc-body text-[11px] text-[#141414]/40 mt-1" />
    </div>
  </div>
);

/* ═══ Generic addable blocks ═══ */
export const TextSectionBlock = ({ id, p }) => (
  <Sec id={id} className="py-16 px-6" bg={p.bg} testid={`block-${id}`}>
    <div className="max-w-3xl mx-auto text-center">
      <Tx v={p.eyebrow} sec={id} k="eyebrow" base="arc-body text-xs font-bold uppercase tracking-[0.25em] text-[#141414]/40 mb-4" />
      <Ext sec={id} p={p} at="eyebrow" />
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight mb-5" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.body} sec={id} k="body" base="arc-body text-base sm:text-lg text-[#141414]/65 leading-relaxed" strongClass="font-bold text-[#141414]" />
      <Ext sec={id} p={p} at="body" />
      {p.show_cta && <div className="mt-8"><Cta cta={p.cta} sec={id} /></div>}
      <Ext sec={id} p={p} at="cta" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const CtaBandBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 py-10" testid={`block-${id}`}>
    <div className="max-w-4xl mx-auto rounded-3xl px-8 py-14 text-center" style={{ background: p.bg || '#141414' }}>
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#F0EFEC] text-3xl sm:text-4xl leading-tight mb-4" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.body} sec={id} k="body" base="arc-body text-base text-[#F0EFEC]/60 mb-9" strongClass="font-bold text-[#F0EFEC]" />
      <Ext sec={id} p={p} at="body" />
      <Cta cta={p.cta} sec={id} />
      <Ext sec={id} p={p} at="cta" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const VideoSectionBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 py-14" bg={p.bg} testid={`block-${id}`}>
    <div className="max-w-3xl mx-auto">
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-2xl sm:text-3xl leading-tight text-center mb-6" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <VideoFrame
        url={p.video_url}
        sec={id} k="video_url"
        className="rounded-3xl overflow-hidden bg-[#141414] shadow-[0_28px_90px_rgba(20,20,20,0.2)]"
        placeholder={(
          <div className="aspect-video flex items-center justify-center">
            <span className="arc-body text-sm font-bold text-white/50">Paste a Wistia link in the editor to add a video</span>
          </div>
        )}
      />
      <Ext sec={id} p={p} at="video_url" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

/* Comparison table — configurable columns/rows with check / cross / dash / text / button cells */
export const TableBlock = ({ id, p }) => {
  const cols = p.cols || [];
  const rows = p.rows || [];
  const gridStyle = { display: 'grid', gridTemplateColumns: cols.map((c) => ({ narrow: '0.7fr', wide: '1.7fr' }[c.width] || '1fr')).join(' ') };
  const hlCls = (c) => (c.highlight ? 'bg-amber-50' : '');
  return (
    <Sec id={id} className="px-6 py-14" bg={p.bg} testid={`block-${id}`}>
      <div className="max-w-4xl mx-auto">
        <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight text-center mb-8" strongClass="font-black" />
        <Ext sec={id} p={p} at="headline" />
        <div className="rounded-3xl border border-black/[0.06] bg-white overflow-hidden shadow-[0_12px_40px_rgba(20,20,20,0.08)]" data-testid={`table-${id}`}>
          <div style={gridStyle} className="border-b border-black/[0.08]">
            {cols.map((c, ci) => (
              <div key={ci} className={`px-4 py-4 ${ci === 0 ? 'text-left' : 'text-center'} ${hlCls(c)} ${c.highlight ? 'border-t-4 border-amber-400' : ''}`}>
                <span className={`arc-head block font-black ${ci === 0 ? 'text-xs uppercase tracking-[0.2em] text-[#141414]/45 pt-1.5' : 'text-lg text-[#141414]'}`}>
                  {c.highlight && ci !== 0 && <Star size={13} className="inline -mt-1 mr-1.5 text-amber-500" fill="currentColor" />}
                  <Tx v={c.header} sec={id} k={`cols.${ci}.header`} as="span" base="" />
                </span>
                {String(c.sub || '') !== '' && (
                  <span className="arc-body block text-[11px] font-semibold text-[#141414]/45 mt-0.5">
                    <Tx v={c.sub} sec={id} k={`cols.${ci}.sub`} as="span" base="" />
                  </span>
                )}
              </div>
            ))}
          </div>
          {rows.map((r, ri) => (
            <div key={ri} style={gridStyle} className={ri > 0 ? 'border-t border-black/[0.05]' : ''}>
              {cols.map((c, ci) => {
                const cell = (r.cells || [])[ci] || { kind: 'dash' };
                return (
                  <div key={ci} className={`px-4 py-3.5 flex items-center ${ci === 0 ? 'justify-start' : 'justify-center'} ${hlCls(c)}`}>
                    {cell.kind === 'check' && (
                      <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0"><Check size={13} className="text-white" strokeWidth={3.5} /></span>
                    )}
                    {cell.kind === 'x' && (
                      <span className="w-6 h-6 rounded-full bg-rose-500/90 flex items-center justify-center shrink-0"><XIcon size={12} className="text-white" strokeWidth={3.5} /></span>
                    )}
                    {cell.kind === 'dash' && <Minus size={14} className="text-[#141414]/25" />}
                    {cell.kind === 'button' && (
                      <Cta cta={cell.v} sec={id} k={`rows.${ri}.cells.${ci}.v`} arrow={false} className="!px-5 !py-2.5 !text-sm md:!text-sm w-full max-w-[220px]" testid={`tbl-btn-${id}-${ri}-${ci}`} />
                    )}
                    {(cell.kind === 'text' || !cell.kind) && (
                      <Tx
                        v={cell}
                        sec={id}
                        k={`rows.${ri}.cells.${ci}`}
                        as="span"
                        base={`arc-body text-sm ${ci === 0 ? 'font-medium text-[#141414]/80' : 'font-bold text-[#141414]/70 text-center'}`}
                        strongClass="font-black text-[#141414]"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <Ext sec={id} p={p} at="end" />
      </div>
    </Sec>
  );
};

/* Image slider — 1–2 marquee rows of landscape images (top slides left, bottom right) */
const IMG_H = { sm: 'h-32 sm:h-36', md: 'h-40 sm:h-48', lg: 'h-52 sm:h-64' };
export const ImageMarqueeBlock = ({ id, p }) => {
  const edit = useContext(EditCtx);
  const top = (p.images_top || []).filter(Boolean);
  const bottom = (p.images_bottom || []).filter(Boolean);
  const h = IMG_H[p.size || 'md'] || IMG_H.md;
  const loop = (arr) => (arr.length >= 6 ? [...arr, ...arr] : [...arr, ...arr, ...arr, ...arr]);
  const row = (imgs, dir, k) => (
    <Editable sec={id} k={k}>
      <div className="overflow-hidden">
        <div className={`flex gap-4 w-max ${dir}`} data-testid={`img-row-${k}-${id}`}>
          {loop(imgs).map((src, i) => (
            <img key={i} src={src} alt="" loading="lazy" draggable={false} className={`${h} aspect-[16/10] object-cover rounded-2xl border border-black/[0.06] shadow-[0_10px_30px_rgba(20,20,20,0.12)]`} />
          ))}
        </div>
      </div>
    </Editable>
  );
  return (
    <Sec id={id} className="py-14 overflow-hidden" bg={p.bg} testid={`block-${id}`}>
      <div className="px-6">
        <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight text-center mb-8" strongClass="font-black" />
        <Ext sec={id} p={p} at="headline" />
      </div>
      <div className="space-y-4">
        {top.length > 0 && row(top, 'arc-marquee-track', 'images_top')}
        {bottom.length > 0 && row(bottom, 'arc-marquee-track-rev', 'images_bottom')}
        {top.length === 0 && bottom.length === 0 && edit && (
          <Editable sec={id} k="images_top">
            <div className="mx-6 rounded-2xl border-2 border-dashed border-black/15 py-12 text-center">
              <span className="arc-body text-sm font-bold text-[#141414]/40">Add images in the left panel — the top row slides left, the bottom row slides right</span>
            </div>
          </Editable>
        )}
      </div>
      <div className="px-6"><Ext sec={id} p={p} at="end" /></div>
    </Sec>
  );
};

/* Side-by-side plan cards — glow + badge on the recommended one, everything editable */
export const PlansBlock = ({ id, p }) => {
  const cards = p.cards || [];
  return (
    <Sec id={id} className="px-6 py-16" bg={p.bg || '#141414'} testid={`block-${id}`}>
      <div className="max-w-5xl mx-auto">
        <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#F0EFEC] text-3xl sm:text-4xl leading-tight text-center" strongClass="font-black" />
        <Ext sec={id} p={p} at="headline" />
        <Tx v={p.subhead} sec={id} k="subhead" base="arc-body text-base text-[#F0EFEC]/65 text-center mt-3 mb-12 max-w-2xl mx-auto" strongClass="font-bold text-[#F0EFEC]" />
        <Ext sec={id} p={p} at="subhead" />
        <div className="grid gap-6 items-stretch md:[grid-template-columns:repeat(var(--vx-plans),minmax(0,1fr))]" style={{ '--vx-plans': Math.min(cards.length || 1, 3) }}>
          {cards.map((c, i) => (
            <div
              key={i}
              className="relative rounded-3xl p-8 pt-10 flex flex-col"
              style={{
                background: c.bg || '#FFFFFF',
                color: c.fg || '#141414',
                ...(c.highlight
                  ? { border: '2px solid #F5C518', boxShadow: '0 0 70px rgba(245,197,24,0.35)' }
                  : { border: '2px solid rgba(20,20,20,0.08)' }),
              }}
              data-testid={`plan-card-${id}-${i}`}
            >
              {c.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="arc-body inline-block rounded-lg bg-[#F5C518] text-[#141414] text-[11px] font-black tracking-[0.15em] uppercase px-4 py-1.5 shadow-lg whitespace-nowrap">
                    <Tx v={c.badge || 'Recommended'} sec={id} k={`cards.${i}.badge`} as="span" base="" />
                  </span>
                </div>
              )}
              <Tx v={c.title} sec={id} k={`cards.${i}.title`} as="h3" base="arc-head text-xl sm:text-2xl font-black text-center" strongClass="font-black" />
              <Tx v={c.price} sec={id} k={`cards.${i}.price`} base="arc-head text-5xl font-black text-center mt-4" strongClass="font-black" />
              <Tx v={c.price_note} sec={id} k={`cards.${i}.price_note`} base="arc-body text-xs font-bold uppercase tracking-[0.15em] text-center mt-1.5 opacity-60" />
              {String(c.chip || '') !== '' && (
                <div className="text-center mt-3">
                  <span className="arc-body inline-block rounded-lg bg-[#F5C518] text-[#141414] text-[11px] font-black px-3 py-1">
                    <Tx v={c.chip} sec={id} k={`cards.${i}.chip`} as="span" base="" />
                  </span>
                </div>
              )}
              <div className="h-px my-6" style={{ background: 'currentColor', opacity: 0.12 }} />
              <ul className="space-y-3 flex-1">
                {(c.features || []).map((ft, fi) => (
                  <li key={fi} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 shrink-0" style={{ background: c.highlight ? '#F5C518' : '#2563EB' }}>
                      <Check size={11} strokeWidth={3.5} className={c.highlight ? 'text-[#141414]' : 'text-white'} />
                    </span>
                    <span className="arc-body text-sm font-semibold leading-relaxed">
                      <Tx v={ft} sec={id} k={`cards.${i}.features.${fi}`} as="span" base="" strongClass="font-black" />
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                <Cta cta={c.cta} sec={id} k={`cards.${i}.cta`} arrow={false} className="w-full !text-base" testid={`plan-cta-${id}-${i}`} />
              </div>
              {String(c.foot || '') !== '' && (
                <Tx v={c.foot} sec={id} k={`cards.${i}.foot`} base="arc-body text-xs text-center mt-3 opacity-60" />
              )}
            </div>
          ))}
        </div>
        <Ext sec={id} p={p} at="end" />
      </div>
    </Sec>
  );
};

export const SpacerBlock = ({ id, p }) => (
  <Sec id={id} className={{ sm: 'h-8', md: 'h-16', lg: 'h-28' }[p.size || 'md']} bg={p.bg} testid={`block-${id}`}>
    {null}
  </Sec>
);
